import React, { Fragment } from 'react';
import Link from 'shared/SlashLink';
import usePageSeo from '../../hooks/usePageSeo';
import Header from '../../components/header/Header';
import PageTitle from '../../components/pagetitle/PageTitle';
import Scrollbar from '../../components/scrollbar/scrollbar';
import Footer from '../../components/footer/Footer';
import CtaSection from '../../components/CtaSection/CtaSection';
import { BOOKING_URL, BOOKING_ENABLED } from '../../config/booking';
import { trackEvent } from '../../utils/analytics';

const ClickHandler = () => window.scrollTo(10, 0);

// What we build — capability cards. Trading Indicator Development is one named specialization.
const CAPABILITIES = [
    {
        icon: 'fa-chart-line',
        title: 'Trading Indicator Development',
        desc: 'Custom TradingView (Pine Script v6) indicators, screeners, dashboards, and alerts — plus MQL/ThinkScript conversions. Transparent, price-based logic with educational tooltips. Built for clarity, not black boxes.',
        tag: 'Pine Script · TradingView',
    },
    {
        icon: 'fa-calculator',
        title: 'Calculators & Decision Tools',
        desc: 'Interactive ROI models, feasibility pro formas, scoring estimators, and cost calculators that turn a spreadsheet nobody maintains into a clean web tool your team and clients actually use.',
        tag: 'Models · Estimators',
    },
    {
        icon: 'fa-gauge-high',
        title: 'Dashboards & Internal Tools',
        desc: 'Data dashboards, searchable databases, and internal apps that pull your operation into one place — filterable, exportable, and shareable with decision-makers.',
        tag: 'Dashboards · Admin apps',
    },
    {
        icon: 'fa-map-location-dot',
        title: 'Data & Mapping Products',
        desc: 'Geospatial maps, pipeline explorers, and large-dataset front-ends — fast search across thousands of records, with clustering, filters, and report generation built in.',
        tag: 'Maps · Search · Reports',
    },
    {
        icon: 'fa-robot',
        title: 'AI & Automation',
        desc: 'AI assistants, workflow automation, and document/report generation wired into your existing tools — reclaiming hours of manual work without ripping out what already works.',
        tag: 'AI · Workflow',
    },
    {
        icon: 'fa-code',
        title: 'Web Apps & Integrations',
        desc: 'Fast, SEO-friendly websites and web apps, API integrations, and lead-capture systems — engineered to load fast, rank well, and convert.',
        tag: 'Web · APIs',
    },
];

// Proof — real tools BUILDLAB designed and shipped (link to live examples on the site).
const PROOF = [
    {
        title: 'California Affordable Housing Pipeline',
        desc: 'A searchable, mapped database of 600+ LIHTC projects with filters, per-developer pages, and a one-click branded ROI report visitors can email to their board.',
        link: '/housing-pipeline',
        cta: 'Explore the tool',
    },
    {
        title: 'Affordable Housing ROI Calculator',
        desc: 'An interactive value model — door-fee income, WiFi value, design savings, and return on investment recompute live as you adjust units, doors, and cameras.',
        link: '/affordable-housing',
        cta: 'Open the calculator',
    },
    {
        title: 'Feasibility Pro Forma & Estimators',
        desc: 'Acquisition pro forma, TCAC scoring, low-voltage budgets, and an IT cost calculator — domain-specific decision tools, each built from scratch.',
        link: '/feasibility-pro-forma',
        cta: 'See the pro forma',
    },
    {
        title: 'BUILDLAB Institutional Indicator',
        desc: 'A transparent, price-only TradingView indicator — VWAP/sigma bands, floor pivots, opening range, market-internals layer, and a plain-English decision panel. Free Pine Script download.',
        link: '/institutional-indicator',
        cta: 'Get the free indicator',
    },
    {
        title: 'CNC Machine Configurator & Order Tracking',
        desc: 'Built for a CNC machine manufacturer — an options-master spreadsheet turned into a guided configurator, branded quote generation, CRM and shop-floor ERP integration, and phase-by-phase order tracking from quote to installed machine.',
        link: '/cnc-machine-builders',
        cta: 'See the CNC solution',
    },
];

// How we work — engagement models
const ENGAGEMENTS = [
    { t: 'Fixed-scope build', d: 'You have a clear brief. We scope it, quote a fixed price, build, test, and hand it off.' },
    { t: 'Discovery + design', d: 'You have the problem, not the spec. We map it, design the solution, then build in phases you approve.' },
    { t: 'Conversion & rescue', d: 'A script or tool in another language or a broken build. We convert, fix, and modernize it.' },
    { t: 'Ongoing partner', d: 'Iterative work on a retainer — new features, maintenance, and support as your needs grow.' },
];

// Process steps
const PROCESS = [
    { n: '01', t: 'Scope', d: 'A short discovery call to pin down what it must do, who uses it, and what success looks like.' },
    { n: '02', t: 'Design', d: 'A clear plan and, where it helps, a working prototype — so you see it before we build it all.' },
    { n: '03', t: 'Build', d: 'Engineered in-house with regular check-ins. No black boxes; you see progress as it ships.' },
    { n: '04', t: 'Launch & support', d: 'Tested, documented, and handed off — with support options for what comes next.' },
];

const CustomSoftwarePage = () => {
    usePageSeo({
        title: 'Software Development & Custom Solutions',
        description: 'Custom software built in-house — TradingView indicators, ROI calculators, dashboards, data tools, AI automation, and web apps. By BUILDLAB.',
        keywords: 'custom software development, custom solutions, trading indicator development, Pine Script developer, TradingView indicator, ROI calculator development, dashboard development, web app development, AI automation, BUILDLAB',
    });

    const navy = '#020842';
    const blue = '#0044EB';

    return (
        <Fragment>
            <Header />
            <main id="main-content" className="page_content">
                <PageTitle pageTitle={'Software Development & Custom Solutions'} pagesub={'Solutions'} pageTop={'Custom Software'} />

                {/* INTRO */}
                <section className="section_space bg-light">
                    <div className="container">
                        <div className="row align-items-center mb-2">
                            <div className="col-lg-7">
                                <span style={{ display: 'inline-block', fontSize: '.74rem', fontWeight: 700, letterSpacing: '.09em', textTransform: 'uppercase', color: blue, marginBottom: 10 }}>
                                    Custom Software · Built In-House
                                </span>
                                <h2 className="mb-3">We build the tool when off-the-shelf doesn&rsquo;t fit</h2>
                                <p>
                                    BUILDLAB designs and builds custom software for industries that need it — from a searchable
                                    affordable-housing pipeline with one-click ROI reports, to transparent trading indicators, to
                                    interactive calculators that replace the spreadsheet nobody wants to maintain.
                                </p>
                                <p>
                                    Same team, same standard: clear scope, transparent logic, fast and accessible front-ends, and
                                    something your people and your clients will actually use. No black boxes, no offshore handoffs.
                                </p>
                                <div className="d-flex flex-wrap gap-2 mt-3">
                                    <Link to="/contact" onClick={ClickHandler} className="btn btn-primary">
                                        <span className="btn_label" data-text="Start a project">Start a project</span>
                                        <span className="btn_icon"><i className="fa-solid fa-arrow-up-right" aria-hidden="true"></i></span>
                                    </Link>
                                    {BOOKING_ENABLED && (
                                        <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer" onClick={() => trackEvent('book_consult', { tool: 'Custom Software' })} className="btn btn-outline-dark" style={{ padding: '12px 26px', fontWeight: 600 }}>
                                            Book a 15-min scoping call
                                        </a>
                                    )}
                                </div>
                            </div>
                            <div className="col-lg-5 mt-4 mt-lg-0">
                                <div style={{ background: navy, borderRadius: 18, padding: '28px 30px', color: '#fff' }}>
                                    <div style={{ fontSize: '.8rem', opacity: .8, marginBottom: 14, fontWeight: 600, letterSpacing: '.04em' }}>WHAT THAT LOOKS LIKE</div>
                                    {[
                                        ['600+', 'LIHTC projects in a live, searchable pipeline tool we built'],
                                        ['1-click', 'branded ROI report, generated and emailed from the browser'],
                                        ['Pine v6', 'transparent TradingView indicators, screeners & alerts'],
                                        ['In-house', 'engineering — no black boxes, no offshore handoffs'],
                                    ].map(([big, small]) => (
                                        <div key={small} style={{ display: 'flex', gap: 14, alignItems: 'baseline', padding: '10px 0', borderTop: '1px solid rgba(255,255,255,.12)' }}>
                                            <span style={{ fontSize: '1.4rem', fontWeight: 800, color: '#F3A338', minWidth: 92 }}>{big}</span>
                                            <span style={{ fontSize: '.9rem', opacity: .9 }}>{small}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* WHAT WE BUILD */}
                <section className="section_space">
                    <div className="container">
                        <div className="section_space_sm text-center pt-0" style={{ maxWidth: 760, margin: '0 auto 36px' }}>
                            <span style={{ fontSize: '.74rem', fontWeight: 700, letterSpacing: '.09em', textTransform: 'uppercase', color: blue }}>What we build</span>
                            <h2 className="mt-2">Custom software, across domains</h2>
                            <p className="text-muted">Pick the one that fits — or bring us the problem and we&rsquo;ll tell you what it should be.</p>
                        </div>
                        <div className="row">
                            {CAPABILITIES.map((c) => (
                                <div className="col-lg-4 col-md-6 mb-4" key={c.title}>
                                    <div style={{ background: '#fff', border: '1px solid var(--bs-border-color)', borderRadius: 16, padding: '26px 26px', height: '100%' }}>
                                        <span style={{ width: 50, height: 50, borderRadius: 12, background: 'var(--bs-primary-bg-subtle, #e7eeff)', color: blue, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, marginBottom: 16 }}>
                                            <i className={`fa-solid ${c.icon}`} aria-hidden="true"></i>
                                        </span>
                                        <h3 className="h5 mb-2" style={{ color: navy }}>{c.title}</h3>
                                        <p style={{ fontSize: '.9rem', marginBottom: 14 }}>{c.desc}</p>
                                        <span style={{ fontSize: '.72rem', fontWeight: 700, letterSpacing: '.04em', textTransform: 'uppercase', color: 'var(--bs-secondary-color)' }}>{c.tag}</span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* PROOF */}
                <section className="section_space bg-light">
                    <div className="container">
                        <div className="section_space_sm text-center pt-0" style={{ maxWidth: 760, margin: '0 auto 36px' }}>
                            <span style={{ fontSize: '.74rem', fontWeight: 700, letterSpacing: '.09em', textTransform: 'uppercase', color: blue }}>Proof, not promises</span>
                            <h2 className="mt-2">Tools we designed and shipped</h2>
                            <p className="text-muted">Every one of these is real, live software BUILDLAB built — explore them yourself.</p>
                        </div>
                        <div className="row">
                            {PROOF.map((p) => (
                                <div className="col-lg-6 mb-4" key={p.title}>
                                    <div style={{ background: '#fff', border: '1px solid var(--bs-border-color)', borderRadius: 16, padding: '26px 28px', height: '100%', display: 'flex', flexDirection: 'column' }}>
                                        <h3 className="h5 mb-2" style={{ color: navy }}>{p.title}</h3>
                                        <p style={{ fontSize: '.9rem', flex: 1 }}>{p.desc}</p>
                                        {p.link ? (
                                            <Link to={p.link} onClick={ClickHandler} style={{ fontWeight: 700, color: blue, fontSize: '.9rem' }}>
                                                {p.cta} <i className="fa-solid fa-arrow-right" aria-hidden="true" style={{ fontSize: '.78rem' }}></i>
                                            </Link>
                                        ) : (
                                            <span style={{ fontWeight: 600, color: 'var(--bs-secondary-color)', fontSize: '.86rem' }}>{p.cta}</span>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* PROCESS */}
                <section className="section_space">
                    <div className="container">
                        <div className="section_space_sm text-center pt-0" style={{ maxWidth: 760, margin: '0 auto 36px' }}>
                            <span style={{ fontSize: '.74rem', fontWeight: 700, letterSpacing: '.09em', textTransform: 'uppercase', color: blue }}>How it works</span>
                            <h2 className="mt-2">From idea to shipped, in four steps</h2>
                        </div>
                        <div className="row">
                            {PROCESS.map((s) => (
                                <div className="col-lg-3 col-md-6 mb-4" key={s.n}>
                                    <div style={{ height: '100%', padding: '4px 8px' }}>
                                        <div style={{ fontSize: '2rem', fontWeight: 800, color: 'var(--bs-primary-bg-subtle, #cfe0ff)', lineHeight: 1 }}>{s.n}</div>
                                        <h3 className="h6 mt-2 mb-2" style={{ color: navy }}>{s.t}</h3>
                                        <p style={{ fontSize: '.88rem' }}>{s.d}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* ENGAGEMENT MODELS */}
                <section className="section_space bg-light">
                    <div className="container">
                        <div className="row align-items-center">
                            <div className="col-lg-4 mb-4 mb-lg-0">
                                <span style={{ fontSize: '.74rem', fontWeight: 700, letterSpacing: '.09em', textTransform: 'uppercase', color: blue }}>Engagement models</span>
                                <h2 className="mt-2">Work with us the way that fits</h2>
                                <p className="text-muted">Not sure which? Start with a scoping call and we&rsquo;ll recommend the lightest path to a working tool.</p>
                            </div>
                            <div className="col-lg-8">
                                <div className="row">
                                    {ENGAGEMENTS.map((e) => (
                                        <div className="col-md-6 mb-3" key={e.t}>
                                            <div style={{ background: '#fff', border: '1px solid var(--bs-border-color)', borderRadius: 14, padding: '20px 22px', height: '100%' }}>
                                                <h3 className="h6 mb-1" style={{ color: navy }}>{e.t}</h3>
                                                <p style={{ fontSize: '.86rem', margin: 0 }}>{e.d}</p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                        <p className="small text-muted mt-4" style={{ maxWidth: 880 }}>
                            Trading tools are provided for educational and informational purposes only and are not financial advice, a
                            recommendation, or a solicitation to buy or sell any security. BUILDLAB builds software; trading decisions and
                            their outcomes are your own.
                        </p>
                    </div>
                </section>

                <CtaSection />
            </main>
            <Footer />
            <Scrollbar />
        </Fragment>
    );
};

export default CustomSoftwarePage;
